export type SearchResultIdentityInput = {
	url?: string;
	title?: string;
	snippet?: string;
	image?: string;
	imageUrl?: string;
	displayLink?: string;
	type?: string;
};

const TRACKING_PARAM = /^(utm_|fbclid|gclid|mc_cid|mc_eid|igshid)/i;
// Image providers frequently return the same original file with different
// display transforms. These parameters change presentation, not identity.
const IMAGE_PRESENTATION_PARAM = /^(?:w|h|width|height|q|quality|dpr|fit|crop|resize|format|fm|auto|ixlib)$/i;

function compactText(value?: string): string {
	return (value || '').replace(/\s+/g, ' ').trim().toLowerCase();
}

export function normalizeSearchResultUrl(url?: string): string {
	const trimmed = (url || '').trim();
	if (!trimmed) return '';
	try {
		const parsed = new URL(trimmed.includes('://') ? trimmed : `https://${trimmed}`);
		parsed.hash = '';
		parsed.hostname = parsed.hostname.replace(/^www\./i, '').toLowerCase();
		if (parsed.protocol === 'http:') parsed.protocol = 'https:';
		const params = new URLSearchParams(parsed.search);
		for (const key of [...params.keys()]) {
			if (TRACKING_PARAM.test(key) || IMAGE_PRESENTATION_PARAM.test(key)) params.delete(key);
		}
		parsed.search = params.toString();
		if (parsed.pathname.length > 1 && parsed.pathname.endsWith('/')) {
			parsed.pathname = parsed.pathname.slice(0, -1);
		}
		return parsed.toString().toLowerCase();
	} catch {
		return trimmed.replace(/\/+$/, '').toLowerCase();
	}
}

function prefixedIdentity(prefix: 'url' | 'image' | 'text', value: string): string {
	return value ? `${prefix}:${value}` : '';
}

/**
 * Return every stable identity available for a result, ordered from strongest
 * to weakest. Search providers often wrap one image in several URLs, so using
 * only the first URL allows visible duplicates to escape the shared dedupe pass.
 */
export function searchResultIdentities(entry: SearchResultIdentityInput): string[] {
	const identities = new Set<string>();
	const add = (prefix: 'url' | 'image' | 'text', value?: string) => {
		const identity = prefixedIdentity(prefix, value || '');
		if (identity) identities.add(identity);
	};

	const normalizedUrl = normalizeSearchResultUrl(entry.url);
	const normalizedImage = normalizeSearchResultUrl(entry.image || entry.imageUrl);
	if (entry.type === 'image') {
		add('image', normalizedImage || normalizedUrl);
	} else {
		add('url', normalizedUrl);
		add('image', normalizedImage);
	}

	const title = compactText(entry.title);
	const snippet = compactText(entry.snippet);
	// A title+snippet fingerprint catches syndicated pages that have different
	// canonical URLs. For image results providers often omit snippets, so an
	// exact, sufficiently descriptive title is the useful fallback.
	if (title && snippet) {
		add('text', `${title}|${snippet}`);
	} else if (entry.type === 'image' && !normalizedImage && !normalizedUrl && title.length >= 16) {
		add('text', `image-title:${title}`);
	} else if (!normalizedUrl && !normalizedImage) {
		add('text', [title, snippet, compactText(entry.displayLink)].filter(Boolean).join('|'));
	}

	return [...identities];
}

export function searchResultIdentity(entry: SearchResultIdentityInput): string {
	return searchResultIdentities(entry)[0] || '';
}

export function keepUnseenSearchResult(
	seen: Set<string>,
	entry: SearchResultIdentityInput
): boolean {
	const identities = searchResultIdentities(entry);
	if (!identities.length || identities.some((identity) => seen.has(identity))) return false;
	identities.forEach((identity) => seen.add(identity));
	return true;
}

export function dedupeSearchEntries<T extends SearchResultIdentityInput>(entries: T[]): T[] {
	const seen = new Set<string>();
	const unique: T[] = [];
	for (const entry of entries) {
		if (!keepUnseenSearchResult(seen, entry)) continue;
		unique.push(entry);
	}
	return unique;
}
